import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Mail, CheckCircle, XCircle, RefreshCw } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EmailVerification = () => {
  const [status, setStatus] = useState('pending'); // pending, success, error
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const email = location.state?.email || localStorage.getItem('signup_email');
  const role = localStorage.getItem('signup_role') || 'member';
  const requiresPayment = location.state?.requiresPayment;
  const token = searchParams.get('token');

  useEffect(() => {
    setStatus('success');
    setMessage('Email Verified! You can now proceed to payment.');
  }, []);

  useEffect(() => {
    // ⏳ jab tak email query me nahi → loader chalta rahe
   console.log(email)
    verifyEmail();
    // eslint-disable-next-line
  }, [email]);
  console.log(email)
  const verifyEmail = async () => {
    try {
      const response = await axios.post(
        `${API}/auth/verify-email-by-email`,
        null,
        {
          params: { email_address: email }
        }
      );

      if (response.data?.success) {
        navigate('/email-verification', {
          state: {
            email,
            requiresPayment: requiresPayment || false,
            message:
              response.data?.message ||
              'Your email has been verified successfully'
          }
        });
      } else {
        setError('Email verification failed');
      }
    } catch (err) {
      let errorMessage = 'Email verification failed';

      const detail = err.response?.data?.detail;

      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (Array.isArray(detail)) {
        errorMessage = detail[0]?.msg || errorMessage;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };
  // const verifyEmail = async (verificationToken) => {
  //   if (!verificationToken) return;

  //   setLoading(true);
  //   try {
  //     // Email verification is now skipped - automatically set success
  //     setStatus('success');
  //     setMessage('Email verification skipped! You can now proceed to payment.');

  //     // Clear the token from URL
  //     window.history.replaceState({}, document.title, window.location.pathname);

  //   } catch (error) {
  //     console.error('Email verification error:', error);
  //     setStatus('error');
  //     setMessage(error.response?.data?.detail || 'Email verification failed');
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const proceedToPayment = async () => {
    setLoading(true);
    try {
      const origin = window.location.origin;
      const signupEmail = email || localStorage.getItem('signup_email');

      const response = await axios.post(`${API}/payments/checkout`,
        new URLSearchParams({
          package_id: role,
          origin_url: origin,
          user_email: signupEmail
        }),
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
          }
        }
      );

      // Store session info for tracking
      localStorage.setItem('checkout_session_id', response.data.session_id);

      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;

    } catch (error) {
      console.error('Payment error:', error);
      console.error('Payment error details:', error.response?.data);
      setMessage(`Failed to create payment session: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const renderContent = () => {
    if (!token && status === 'pending' && !requiresPayment) {
      return (
        <div className="text-center">
          <Mail className="w-16 h-16 text-blue-500 mx-auto mb-6" />
          <h3 className="text-2xl font-semibold text-white mb-4">Email Verification</h3>
          <p className="text-gray-300 mb-6">
            Registration completed for:
          </p>
          <p className="text-blue-400 font-medium mb-6 break-words">{email}</p>
          <p className="text-gray-300 text-sm mb-8">
            Email verification is no longer required. You can proceed directly to the next step.
          </p>

          <div className="space-y-4">
            <Button
              onClick={() => navigate('/login')}
              variant="outline"
              className="border-gray-600 text-gray-300 hover:bg-gray-800"
            >
              Back to Login
            </Button>
          </div>
        </div>
      );
    }

    if (loading) {
      return (
        <div className="text-center">
          <RefreshCw className="w-16 h-16 text-blue-500 mx-auto mb-6 animate-spin" />
          <h3 className="text-2xl font-semibold text-white mb-4">Verifying Email...</h3>
          <p className="text-gray-300">Please wait while we verify your email address.</p>
        </div>
      );
    }

    if (status === 'success') {
      return (
        <div className="text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-6" />
          <h3 className="text-2xl font-semibold text-white mb-4">Email Verified!</h3>
          <p className="text-gray-300 mb-6">{message}</p>

          {requiresPayment && (
            <div className="bg-gray-800 border border-gray-700 rounded-lg p-6 mb-6">
              <h4 className="text-lg font-semibold text-white mb-4">Complete Your Membership</h4>
              <div className="flex items-center justify-between mb-4">
                <span className="text-gray-300">Success Club {role === 'host' ? 'Host' : 'Member'} Membership</span>
                <span className="text-2xl font-bold text-amber-500">$15/month</span>
              </div>
              <p className="text-gray-400 text-sm mb-4">
                Includes magazine access and all platform features
              </p>
              <Button
                onClick={proceedToPayment}
                disabled={loading}
                className="w-full bg-amber-600 hover:bg-amber-700 text-white"
              >
                {loading ? 'Redirecting...' : 'Complete Payment'}
              </Button>
            </div>
          )}

          <div className="space-y-2">
            <Button
              onClick={() => navigate(`/login/${role}`)}
              variant="outline"
              className="border-gray-600 text-gray-300 hover:bg-gray-800"
            >
              Continue to Login
            </Button>
          </div>
        </div>
      );
    }

    if (status === 'error') {
      return (
        <div className="text-center">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-6" />
          <h3 className="text-2xl font-semibold text-white mb-4">Verification Failed</h3>
          <p className="text-gray-300 mb-6">{message}</p>

          <div className="space-y-2">
            <Button
              onClick={() => navigate('/auth')}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              Back to Sign Up
            </Button>
          </div>
        </div>
      );
    }
  };

  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Card className="bg-gray-900 border-gray-800">
          <CardHeader className="text-center">
            <div className="flex items-center justify-center mb-4">
              <div className="w-20 h-20 flex items-center justify-center bg-white rounded-full shadow-lg overflow-hidden" style={{ borderRadius: '50%', width: '80px', height: '80px' }}>
                <img
                  src="https://customer-assets.emergentagent.com/job_refined-login/artifacts/7hacqtlv_Success%20Club%20Logo%20Files%20-%203%20%281%29.png"
                  alt="Success Club Logo"
                  style={{ width: '100%', height: '100%', objectFit: 'contain', scale: '1.1' }}
                />
              </div>
            </div>
            <CardTitle className="text-2xl text-white">
              Email Verification
            </CardTitle>
          </CardHeader>
          <CardContent>
            {renderContent()}
          </CardContent>
        </Card>
      </div>

      {/* Footer */}
      <footer className="auth-footer">
        <div className="auth-footer-content">
          <div className="auth-footer-links">
            <a href="/features">Features</a>
            <a href="/events">Events</a>
            <a href="/magazine">Magazine</a>
            <a href="/pricing">Pricing</a>
            <a href="/contact">Contact</a>
            <a href="/help-center">Help</a>
            <a href="/privacy-policy">Privacy</a>
          </div>
          <div className="auth-footer-copy">
            © 2025 Success Club. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default EmailVerification;